// src/Components/Documents/FolderPickerDialog.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Breadcrumbs,
  Link,
  Typography,
  Box,
  Divider
} from '@mui/material';
import {
  Folder as FolderIcon,
  Home as HomeIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';

/**
 * FolderPickerDialog Component
 *
 * A dialog for selecting a destination folder when moving files
 *
 * @param {boolean} open - Whether the dialog is open
 * @param {function} onClose - Callback when dialog closes
 * @param {function} onSelect - Callback when folder is selected (receives folderPath)
 * @param {Array} allDocuments - All documents/folders to build folder tree from
 * @param {string} currentPath - Current folder path (to exclude from destination options)
 * @param {string} title - Dialog title (default: "Move to Folder")
 */
const FolderPickerDialog = ({
  open,
  onClose,
  onSelect,
  allDocuments = [],
  currentPath = '/',
  title = 'Move to Folder'
}) => {
  const [selectedPath, setSelectedPath] = useState('/');
  const [availableFolders, setAvailableFolders] = useState([]);

  // Build folder tree from allDocuments
  useEffect(() => {
    if (!open) {
      setSelectedPath('/');
      return;
    }

    // Extract all folders from documents
    const folders = allDocuments
      .filter(doc => doc.type === 'folder')
      .map(folder => {
        const parentPath = folder.folderPath || '/';
        const folderName = folder.name || folder.title;
        const fullPath = parentPath === '/' ? `/${folderName}` : `${parentPath}/${folderName}`;

        return {
          id: folder.id,
          name: folderName,
          path: fullPath,
          parentPath: parentPath
        };
      });

    setAvailableFolders(folders);
  }, [open, allDocuments]);

  // Get folders in the current selected path
  const getFoldersInPath = (path) => {
    return availableFolders.filter(folder => folder.parentPath === path);
  };

  // Get breadcrumb path segments
  const getBreadcrumbs = () => {
    if (selectedPath === '/') return [{ name: 'Root', path: '/' }];

    const segments = selectedPath.split('/').filter(Boolean);
    const breadcrumbs = [{ name: 'Root', path: '/' }];

    let currentPath = '';
    segments.forEach((segment, index) => {
      currentPath += '/' + segment;
      breadcrumbs.push({
        name: segment,
        path: currentPath
      });
    });

    return breadcrumbs;
  };

  const handleFolderClick = (folderPath) => {
    setSelectedPath(folderPath);
  };

  const handleBreadcrumbClick = (path) => {
    setSelectedPath(path);
  };

  const handleSelectCurrent = () => {
    onSelect(selectedPath);
    onClose();
  };

  const handleCancel = () => {
    setSelectedPath('/');
    onClose();
  };

  const foldersInCurrentPath = getFoldersInPath(selectedPath);
  const breadcrumbs = getBreadcrumbs();

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { minHeight: '400px', maxHeight: '600px' }
      }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {/* Breadcrumbs */}
        <Box sx={{ px: 3, py: 2, bgcolor: 'background.default' }}>
          <Breadcrumbs separator={<ChevronRightIcon fontSize="small" />}>
            {breadcrumbs.map((crumb, index) => (
              <Link
                key={crumb.path}
                component="button"
                variant="body2"
                onClick={() => handleBreadcrumbClick(crumb.path)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  textDecoration: 'none',
                  color: index === breadcrumbs.length - 1 ? 'text.primary' : 'primary.main',
                  fontWeight: index === breadcrumbs.length - 1 ? 600 : 400,
                  '&:hover': {
                    textDecoration: index === breadcrumbs.length - 1 ? 'none' : 'underline'
                  }
                }}
              >
                {index === 0 && <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />}
                {crumb.name}
              </Link>
            ))}
          </Breadcrumbs>
        </Box>

        <Divider />

        {/* Folder List */}
        <List sx={{ pt: 0 }}>
          {foldersInCurrentPath.length === 0 ? (
            <ListItem>
              <ListItemText
                primary="No subfolders"
                secondary="This folder has no subfolders. Click 'Move Here' to move items to this location."
                primaryTypographyProps={{ color: 'text.secondary', fontStyle: 'italic' }}
              />
            </ListItem>
          ) : (
            foldersInCurrentPath.map((folder) => (
              <ListItem key={folder.id} disablePadding>
                <ListItemButton onClick={() => handleFolderClick(folder.path)}>
                  <ListItemIcon>
                    <FolderIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={folder.name}
                    secondary={`Path: ${folder.path}`}
                  />
                </ListItemButton>
              </ListItem>
            ))
          )}
        </List>

        {/* Current Selection Info */}
        <Box sx={{ px: 3, py: 2, bgcolor: 'action.hover', mt: 'auto' }}>
          <Typography variant="caption" color="text.secondary">
            Selected Destination:
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {selectedPath === '/' ? 'Root Folder' : selectedPath}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSelectCurrent}
          disabled={selectedPath === currentPath}
        >
          Move Here
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FolderPickerDialog;
